import { useMutation } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

import useReportsAmplitude from '../useReportsAmplitude';
import GenerateAdministrationReportMutation from './graphql/GenerateAdministrationReport.v4.b2b.graphql';
import type {
  GenerateAdministrationReport as GenerateReport,
  GenerateAdministrationReportVariables as GenerateReportVariables,
} from './graphql/types/GenerateAdministrationReport';

enum STATUS {
  SUCCESS = 'success',
  FAILED = 'error',
}

const useSendAdminReport = (slug: string) => {
  const { t } = useTranslation();
  const { openSnackbar } = useAdminDashboard();
  const { logSubmit } = useReportsAmplitude(slug);
  const [generateAdministrationReport] = useMutation<
    GenerateReport,
    GenerateReportVariables
  >(GenerateAdministrationReportMutation, {
    context: { clientName: 'v4.b2b' },
    onCompleted({ userReportCall }) {
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (!userReportCall?.success)
        openSnackbar({
          type: 'alert',
          msg: t('SNACKBAR_DEFAULT_ERROR'),
        });
    },
    onError() {
      openSnackbar({
        type: 'alert',
        msg: t('SNACKBAR_DEFAULT_ERROR'),
      });
    },
  });

  const submitReport = async (variables: GenerateReportVariables) => {
    const { data } = await generateAdministrationReport({
      variables,
    });
    logSubmit({
      event: 'Generate Administration Report',
      reportType: 'Users Report',
      filters: variables,
      status: data?.userReportCall?.success ? STATUS.SUCCESS : STATUS.FAILED,
    });
    return data;
  };

  return {
    submitReport,
  };
};

export default useSendAdminReport;
